import { useMutation, useQueryClient } from '@tanstack/react-query';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import {
  StatusCode,
  patchApiV2CustomersCustomerId,
  type CustomerSelf,
} from '@hungryroot/rest';
import {
  CUSTOMER_KEY,
  invalidateCustomerData,
} from '../../queries/customers/useIdentify';

interface PersistSpicePreferenceParams {
  spicyLevel: number | null;
}

export function usePersistSpicePreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ spicyLevel }: PersistSpicePreferenceParams) => {
      const res = await patchApiV2CustomersCustomerId({
        customer: { spicy_level: spicyLevel },
      });

      if (res.status !== StatusCode.SuccessOK) {
        logUnknownErrorResponse('Failed to update spice preference', {
          key: 'usePersistSpicePreference',
          error: JSON.stringify(res.data, null, 2),
        });
        throw new Error('Failed to update spice preference');
      }

      return res.data;
    },
    onMutate: async ({ spicyLevel }) => {
      await queryClient.cancelQueries({ queryKey: CUSTOMER_KEY });

      const previousCustomer = queryClient.getQueryData<{
        data: CustomerSelf;
      }>(CUSTOMER_KEY);

      if (previousCustomer?.data) {
        queryClient.setQueryData(CUSTOMER_KEY, {
          ...previousCustomer,
          data: {
            ...previousCustomer.data,
            spicy_level: spicyLevel,
          },
        });
      }

      return { previousCustomer };
    },
    onError: (_err, _params, context) => {
      if (context?.previousCustomer) {
        queryClient.setQueryData(CUSTOMER_KEY, context.previousCustomer);
      }
    },
    onSuccess: () => {
      invalidateCustomerData(queryClient);
    },
  });
}
